// Manages the lifecycle of chunk meshes: generates and meshes chunks within the
// view distance, disposes those outside it, and re-meshes on block edits. Work
// is frame-budgeted so streaming never stalls the render loop.

import * as THREE from 'three';
import { config } from '../sim/config';
import { CHUNK_SIZE_X, CHUNK_SIZE_Z } from '../sim/constants';
import { chunkKey, worldToChunkX, worldToChunkZ } from '../sim/math';
import { World } from '../sim/world';
import { buildChunkGeometry } from './mesher';
import { createAtlasTexture } from './textures';

interface ChunkMeshes {
  solid: THREE.Mesh | null;
  water: THREE.Mesh | null;
}

export class ChunkRenderer {
  private scene: THREE.Scene;
  private world: World;
  private solidMat: THREE.Material;
  private waterMat: THREE.Material;
  private meshes = new Map<string, ChunkMeshes>();
  private buildQueue: string[] = [];
  private queued = new Set<string>();

  constructor(scene: THREE.Scene, world: World) {
    this.scene = scene;
    this.world = world;

    const atlas = createAtlasTexture();
    this.solidMat = new THREE.MeshLambertMaterial({ map: atlas, vertexColors: true });
    this.waterMat = new THREE.MeshLambertMaterial({
      map: atlas,
      vertexColors: true,
      transparent: true,
      opacity: 0.72,
      depthWrite: false,
    });
  }

  /** Enqueue the chunks around the player and drop far ones. Call each frame. */
  update(playerX: number, playerZ: number, budget = 3): void {
    const pcx = worldToChunkX(Math.floor(playerX));
    const pcz = worldToChunkZ(Math.floor(playerZ));
    const vd = config.viewDistanceChunks;

    // Ensure needed chunks (and their neighbours) exist, nearest first.
    const wanted: { cx: number; cz: number; d: number }[] = [];
    for (let dz = -vd; dz <= vd; dz++) {
      for (let dx = -vd; dx <= vd; dx++) {
        const d = dx * dx + dz * dz;
        if (d > vd * vd) continue;
        wanted.push({ cx: pcx + dx, cz: pcz + dz, d });
      }
    }
    wanted.sort((a, b) => a.d - b.d);
    for (const { cx, cz } of wanted) {
      const key = chunkKey(cx, cz);
      if (!this.meshes.has(key) && !this.queued.has(key)) {
        this.queued.add(key);
        this.buildQueue.push(key);
      }
    }

    // Unload chunks outside the view distance (+1 hysteresis).
    for (const key of [...this.meshes.keys()]) {
      const [cx, cz] = key.split(',').map(Number);
      const dx = cx - pcx;
      const dz = cz - pcz;
      if (dx * dx + dz * dz > (vd + 1) * (vd + 1)) {
        this.disposeChunk(key);
      }
    }

    // Build up to `budget` chunk meshes this frame.
    let built = 0;
    while (this.buildQueue.length > 0 && built < budget) {
      const key = this.buildQueue.shift()!;
      this.queued.delete(key);
      const [cx, cz] = key.split(',').map(Number);
      this.buildChunk(cx, cz);
      built++;
    }
  }

  /** Re-mesh a chunk after a block edit (and its neighbours if on a border). */
  remeshAround(wx: number, wz: number): void {
    const cx = worldToChunkX(wx);
    const cz = worldToChunkZ(wz);
    const lx = ((wx % CHUNK_SIZE_X) + CHUNK_SIZE_X) % CHUNK_SIZE_X;
    const lz = ((wz % CHUNK_SIZE_Z) + CHUNK_SIZE_Z) % CHUNK_SIZE_Z;
    this.buildChunk(cx, cz);
    if (lx === 0) this.buildChunk(cx - 1, cz);
    if (lx === CHUNK_SIZE_X - 1) this.buildChunk(cx + 1, cz);
    if (lz === 0) this.buildChunk(cx, cz - 1);
    if (lz === CHUNK_SIZE_Z - 1) this.buildChunk(cx, cz + 1);
  }

  private buildChunk(cx: number, cz: number): void {
    // Ensure this chunk and its 4 neighbours exist so border faces are correct.
    this.world.ensureChunk(cx, cz);
    this.world.ensureChunk(cx + 1, cz);
    this.world.ensureChunk(cx - 1, cz);
    this.world.ensureChunk(cx, cz + 1);
    this.world.ensureChunk(cx, cz - 1);

    const key = chunkKey(cx, cz);
    this.disposeChunk(key, false);

    const geo = buildChunkGeometry(this.world, cx, cz);
    const meshes: ChunkMeshes = { solid: null, water: null };
    const originX = cx * CHUNK_SIZE_X;
    const originZ = cz * CHUNK_SIZE_Z;

    if (geo.solid) {
      const mesh = new THREE.Mesh(geo.solid, this.solidMat);
      mesh.position.set(originX, 0, originZ);
      this.scene.add(mesh);
      meshes.solid = mesh;
    }
    if (geo.water) {
      const mesh = new THREE.Mesh(geo.water, this.waterMat);
      mesh.position.set(originX, 0, originZ);
      this.scene.add(mesh);
      meshes.water = mesh;
    }
    this.meshes.set(key, meshes);
  }

  private disposeChunk(key: string, forget = true): void {
    const meshes = this.meshes.get(key);
    if (!meshes) return;
    for (const mesh of [meshes.solid, meshes.water]) {
      if (mesh) {
        this.scene.remove(mesh);
        mesh.geometry.dispose();
      }
    }
    if (forget) this.meshes.delete(key);
  }

  get loadedCount(): number {
    return this.meshes.size;
  }

  /** Remove and free every chunk mesh (when leaving the world). */
  dispose(): void {
    for (const key of [...this.meshes.keys()]) this.disposeChunk(key);
    this.buildQueue.length = 0;
    this.queued.clear();
    this.solidMat.dispose();
    this.waterMat.dispose();
  }
}
